import { boot } from 'quasar/wrappers'

import ButtonPrimary from 'src/components/buttons/Primary.vue'
import FieldInput from 'components/inputs/Field.vue'
import FieldSelect from 'components/inputs/Select.vue'
import NotFound from 'components/NotFound.vue'

import moment from 'moment-timezone'
import mixpanel from 'mixpanel-browser';

import { createPinia } from 'pinia'
import { useUser } from 'src/store/user'
import UserComposable from 'src/composable/User';
import { Platform, useMeta } from 'quasar'

import { config as capConfig } from './cap-firebase'
import { config as webConfig } from './firebase';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $filters: {
      titleCase: (v: string) => string;
      sentenceCase: (v: string) => string;
      formatDate: (v: Date|string) => string;
      formatHour: (v: Date|string) => string;
    };
    $moment: typeof moment,
  }
}


function sentenceCase(v: string): string {
  return v.trim()[0].toUpperCase() + v.trim().substr(1).toLowerCase()
}

function titleCase(v: string): string {
  const excludeWords: string[] = ['de']
  const words = v.trim().split(' ')
  let output = ''
  words.forEach((w) => {
    if (w && !excludeWords.includes(w)){
      output += w[0].toUpperCase() + w.substr(1).toLowerCase() + ' '
    } else {
      output += w + ' '
    }
  })
  return output.trim()
}

function formatDate(v: Date|string): string {
  return titleCase(moment(v).format('dddd[,] LL'))
}

function formatHour(v: Date|string): string {
  return moment(v).format('HH:mm')
}

// "async" is optional;
// more info on params: https://v2.quasar.dev/quasar-cli/boot-files
export default boot(({ app, router }) => {
  // Add Components
  app.component('SButtonPrimary', ButtonPrimary)
  app.component('SFieldInput', FieldInput)
  app.component('SFieldSelect', FieldSelect)
  app.component('SNotFound', NotFound)
  app.use(createPinia())
  let token = ''
  switch (process.env.DB_NAME) {
    case 'sandbox':
      console.log('mp sandbox')
      token = '2c0e2a318f43e20ac977322b83b63ec0'
      break;
    case 'sandbox_fhir':
      console.log('mp sandbox fhir')
      token = '2c0e2a318f43e20ac977322b83b63ec0'
      break;
    case 'develop':
      console.log('mp development')
      token = 'ee9ebc0ed307faeefc8f2aa4dd3c200a'
      break
    case 'fhir':
      token = '8c749912dd0160e4611bbfded5de99b5'
      break
    case 'production':
      token = '8c749912dd0160e4611bbfded5de99b5'
      break
  }
  mixpanel.init(
    token,
    {
      debug: process.env.NODE_ENV !== 'production'
    }
  )

  // Add Global Functions
  app.config.globalProperties.$filters = {
    sentenceCase,
    titleCase,
    formatDate,
    formatHour
  }

  moment.locale('es-CL')
  app.config.globalProperties.$moment = moment

  const userStore = useUser();
  const { logInWithJWT } = UserComposable();

  router.beforeEach(async (to, from, next) => {
    const canonicalUrl = 'https://app.snabb.cl'
    const metaData = {
      title: 'Snabb',
      titleTemplate: (title: string) => `${title}`,

      // Meta tags
      meta: {
        // Descripción de la página
        description: {
          name: 'description',
          content:
            'Encuentra y reserva citas médicas, odontológicas, psicológicas y más en tu centro de salud más cercano con Snabb.',
        },
        // Palabras clave relevantes para la página
        keywords: {
          name: 'keywords',
          content:
            'citas médicas, odontología, psicología, kinesiología, centro de salud, Chile, horas médicas, centros de salud, Snabb',
        },

        // Meta tag para especificar el tipo de contenido y juego de caracteres
        equiv: {
          'http-equiv': 'Content-Type',
          content: 'text/html; charset=UTF-8',
        },

        // Meta tags para Open Graph (opcional, se utiliza para compartir en redes sociales)
        ogTitle: {
          property: 'og:title',
          // Puedes personalizar el título de Open Graph si lo deseas
          template(ogTitle: string) {
            return `${ogTitle}`;
          },
        },
        ogDescription: {
          property: 'og:description',
          content:
            'Encuentra y reserva citas médicas, odontológicas, psicológicas y más en tu centro de salud más cercano con Snabb.',
        },
        ogType: {
          property: 'og:type',
          content: 'website',
        },
        ogUrl: {
          property: 'og:url',
          content: canonicalUrl,
        },
        ogImage: {
          property: 'og:image',
          content: 'https://snabb.cl/og-img.jpg',
        },
        canonical: {
          rel: 'canonical',
          href: canonicalUrl,
        },
      },
    }
    useMeta(metaData)
    if (to.query.jwt) {
      const jwt = to.query.jwt;
      await logInWithJWT(jwt.toString())
          .catch(console.error)
          .finally(() => {
            const queries = { ...to.query };
            delete queries.jwt;
            next({ name: to.name || '', query: queries });
          });
      return
    }
    if (!userStore.isLoggedIn && to.meta?.requiresAuth) {
      userStore.next = to.fullPath
      mixpanel.track('Page View Not Authorized', {
        path: to.fullPath,
        name: to.name,
      })
      next('/?logIn=true')
    } else {
      mixpanel.track('Page View', {
        from: from.name,
        path: to.fullPath,
        name: to.name,
        query: to.query
      })
      next()
    }
  })
})

export const useConfig = Platform.is.capacitor ? capConfig : webConfig;
